import { useState, useEffect, SyntheticEvent } from "react";
import axios from "axios";
import { DateField, StatCard, type DataTableColumn } from "@routes-plan/ui";
import { Office } from "../../models/office";
import { Route } from "../../models/route";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString, operationalTimezone } from '../../utils/operationalDateTime';
import { useT } from "../../i18n";
import { setPageTitle } from "../../utils/pageTitle";
import { formatCurrency as companyMoney } from '../../utils/format/currency';
import { OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportStats, ReportTable, RouteMultiSelect } from "./components/ReportLayout";

// Interfaces
interface Client { id: number; idNumber: string; fullName: string; }
interface Loan {
  id: number;
  date: string;
  statusDate: string | null;
  value: number;
  totalValue: number;
  paidValue: number;
  client: Client;
  route?: { id: number; name: string; };
}
interface PaymentApi {
  id: number;
  paymentDate: string;          // "2025-09-01"
  operationDate?: string;       // "2025-09-01T15:23:16.000Z"
  value: number;                // Abono
  discountValue: number;        // Descuento
  loan: Loan;
}
interface RowUI {
  dateRaw: string;
  id: number;
  route: string;
  clientName: string;
  loanValue: number;
  payment: number;
  discount: number;
  date: string; // dd/mm/aaaa
  time: string; // HH:mm:ss
}
interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;          // string para que no se limpie el Select
  selectedCollects: string[];
}
interface ReporteDescuentosProps {
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Utils
const formatCurrency = (value: number): string =>
  companyMoney(value);

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return "";
  const isDateTime = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateString);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const parsed = isDateTime
    ? moment.tz(dateString, "YYYY-MM-DD HH:mm:ss", operationalTimezone())
    : isDateOnly
    ? moment.tz(dateString, "YYYY-MM-DD", operationalTimezone())
    : moment.tz(dateString, operationalTimezone());
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
};
const formatTime = (iso?: string): string => {
  if (!iso) return "";
  const parsed = moment.tz(iso, operationalTimezone());
  return parsed.isValid() ? parsed.format("HH:mm:ss") : "";
};

// Componente
const Discounts: React.FC<ReporteDescuentosProps> = ({
  showAlert, showLoading, hideLoading,
}) => {
  const t = useT();
  setPageTitle('menu.reports.discounts');
  const currentDate = nowOperationalDateString();

  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Route[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);

  // filtro
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedCollects: [],
  });

  // datos UI
  const [rows, setRows] = useState<RowUI[]>([]);
  const [summary, setSummary] = useState({ totalPayments: 0, totalDiscount: 0, count: 0 });
  const [queried, setQueried] = useState(false);

  // Oficinas
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/offices");
        if (data?.data) setOffices(data.data);
      } catch (e) {
        console.error(e);
        showAlert(t("summaryReports.loadOfficesError"), "error");
      }
    })();
  }, [showAlert, t]);

  // Cobros por oficina
  useEffect(() => {
    (async () => {
      if (!filter.officeId) return;
      setLoading(true);
      try {
        const { data } = await axios.get(`/offices/${filter.officeId}/routes`);
        setCollectsOffice(data || []);
        setFilter((prev) => ({ ...prev, selectedCollects: [] }));
        setSelectAll(false);
      } catch (e) {
        console.error(e);
        showAlert(t("summaryReports.loadRoutesError"), "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [filter.officeId, showAlert, t]);

  const updateSelectedCollects = (values: string[]) => {
    setFilter((prev) => ({ ...prev, selectedCollects: values }));
    setSelectAll(values.length === collectsOffice.length && collectsOffice.length > 0);
  };

  const validateForm = (): boolean => {
    if (!filter.officeId) {
      showAlert(t("summaryReports.selectOfficeFirst"), "warning");
      return false;
    }
    if (filter.selectedCollects.length === 0) {
      showAlert(t("summaryReports.selectAtLeastOneRoute"), "warning");
      return false;
    }
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(t("summaryReports.startAfterEnd"), "warning");
      return false;
    }
    return true;
  };

  const columns: DataTableColumn<RowUI>[] = [
    { id: "route", header: t("summaryReports.shared.route"), value: (r) => r.route, sortable: true },
    { id: "clientName", header: t("summaryReports.col.client"), value: (r) => r.clientName, sortable: true },
    { id: "loanValue", header: t("summaryReports.col.loanValue"), value: (r) => r.loanValue || 0, render: (r) => formatCurrency(r.loanValue || 0), align: "right", sortable: true, hideBelow: "sm" },
    { id: "payment", header: t("summaryReports.col.payment"), value: (r) => r.payment || 0, render: (r) => formatCurrency(r.payment || 0), align: "right", sortable: true },
    { id: "discount", header: t("summaryReports.col.discount"), value: (r) => r.discount || 0, render: (r) => formatCurrency(r.discount || 0), align: "right", sortable: true },
    { id: "date", header: t("common.date"), value: (r) => r.dateRaw, render: (r) => r.date, csv: (r) => r.date, sortable: true },
    { id: "time", header: t("summaryReports.col.hour"), value: (r) => r.time, hideBelow: "sm" },
  ];

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    setQueried(true);
    try {
      const routeIds = filter.selectedCollects
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id));

      const payload = {
        startDate: filter.startDate,                  // "YYYY-MM-DD"
        endDate: filter.endDate,                      // "YYYY-MM-DD"
        routeId: routeIds,                            // [ids]
      };

      // Endpoint correcto
      const { data } = await axios.post("/reports/discounts", payload);
      const payments: PaymentApi[] = data?.data || [];

      if (!payments.length) {
        setRows([]);
        setSummary({ totalPayments: 0, totalDiscount: 0, count: 0 });
        showAlert(t("summaryReports.noRecordsInPeriod"), "info");
        return;
      }

      const rowsMapped: RowUI[] = payments.map((p) => ({
        id: p.id,
        route: p?.loan?.route?.name || "",
        clientName: p?.loan?.client?.fullName || "",
        loanValue: p?.loan?.totalValue ?? 0,
        payment: p?.value ?? 0,
        discount: p?.discountValue ?? 0,
        date: formatDate(p?.paymentDate),
        dateRaw: `${p?.paymentDate ?? ""} ${p?.operationDate ?? ""}`,
        time: formatTime(p?.operationDate),
      }));

      const totalPayments = rowsMapped.reduce((acc, r) => acc + (r.payment || 0), 0);
      const totalDiscount = rowsMapped.reduce((acc, r) => acc + (r.discount || 0), 0);

      setRows(rowsMapped);
      setSummary({ totalPayments, totalDiscount, count: rowsMapped.length });
      showAlert(t("summaryReports.recordsFound", { count: rowsMapped.length }), "success");
    } catch (error) {
      console.error("Error obteniendo datos:", error);
      showAlert(t("summaryReports.loadDataError"), "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  return (
    <ReportPage title={t('summaryReports.discounts.title')} description={t('summaryReports.discounts.description')}>
      <ReportFilters
        onSubmit={() => handleSubmit({ preventDefault: () => undefined } as SyntheticEvent)}
        submitDisabled={loading}
        submitButtonProps={{ "data-tour": "discounts-query" }}
      >
        <DateField
          label={t("summaryReports.startDate")}
          data-tour="discounts-start"
          value={filter.startDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, startDate: v ?? "" }))}
          max={minYmd(filter.endDate, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t("summaryReports.endDate")}
          data-tour="discounts-end"
          value={filter.endDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, endDate: v ?? "" }))}
          min={filter.startDate}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={filter.officeId} onChange={(id) => setFilter((prev) => ({ ...prev, officeId: id }))} tourId="discounts-office" />
        <RouteMultiSelect routes={collectsOffice} value={filter.selectedCollects} onChange={updateSelectedCollects} tourId="discounts-routes" />
      </ReportFilters>

      {rows.length > 0 && (
        <ReportStats tourId="discounts-stats">
          <StatCard label={t("summaryReports.discounts.count")} value={summary.count} />
          <StatCard label={t("summaryReports.discounts.totalPayments")} value={formatCurrency(summary.totalPayments)} />
          <StatCard label={t("summaryReports.discounts.totalDiscount")} value={formatCurrency(summary.totalDiscount)} />
        </ReportStats>
      )}

      <ReportResults>
        <ReportTable<RowUI>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading && queried}
          exportFileName={t('summaryReports.discounts.exportFile')}
          tourId="discounts"
          ariaLabel={t('summaryReports.discounts.title')}
          emptyState={<ReportEmpty queried={queried} />}
        />
      </ReportResults>
    </ReportPage>
  );
};

export default Discounts;
